import React, { createRef, useCallback, useMemo } from 'react'
import { View, useWindowDimensions } from 'react-native'
import { BottomSheetBackdrop, BottomSheetModal, BottomSheetView } from '@gorhom/bottom-sheet'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { createComponent, ColorUtils } from 'react-native-small-ui'

import { isWeb, useMaxWidth } from '@/components/WebWrapper'
import { DS } from '@/design-system'
import { translate } from '../../i18n'
import { useGameState } from './useGameState'
import { isNumber } from 'lodash'
import { ReduceMotion } from 'react-native-reanimated'
import { Avatar } from '../../components/ANewDesign/PlayerCell/AvatarTimer/Avatar'
import { Chips } from '../../components/GameAssets/Chips'
import type { Pot } from '@ppwr/poker-engine'
import { create } from 'zustand'
import { normalizePots } from './utils/normalizePots'
import { buildPlayerColorMap } from '../../utils/playerColors'

const Box = createComponent(View, {
  flexGrow: 1
})
const Row = createComponent(View, {
  flexDirection: 'row'
})

export const sidePotSheetRef = createRef<BottomSheetModal>()

// Store to track if sidepot sheet is open (for hiding other modals)
export const useSidePotSheetStore = create<{ isOpen: boolean }>(() => ({ isOpen: false }))

export const useSidePotSheet = () => {
  const open = useCallback(() => {
    useSidePotSheetStore.setState({ isOpen: true })
    sidePotSheetRef.current?.present()
  }, [])

  const close = useCallback(() => {
    useSidePotSheetStore.setState({ isOpen: false })
    sidePotSheetRef.current?.close()
  }, [])

  return {
    ref: sidePotSheetRef,
    open,
    close,
    isOpen: useSidePotSheetStore((s) => s.isOpen)
  }
}

export const SidePotSheet = () => {
  const insets = useSafeAreaInsets()
  const { width: screenWidth } = useWindowDimensions()
  const maxWidth = useMaxWidth()
  const _maxWidth = isNumber(maxWidth) ? maxWidth : screenWidth
  const marginHorizontal = screenWidth > _maxWidth ? (screenWidth - _maxWidth) / 2 : 0

  const { game, players } = useGameState()

  const allPots = normalizePots(game?.pots)
  const mainPot = allPots[0]
  const sidePots = allPots.slice(1)

  const playerColorMap = useMemo(() => {
    const allPlayerIds = new Set<string>()
    allPots.forEach((pot) => {
      pot.eligiblePlayerIds.forEach((id: string) => allPlayerIds.add(id))
    })
    return buildPlayerColorMap([...allPlayerIds])
  }, [allPots])

  const renderBackdrop = useCallback((props: React.ComponentProps<typeof BottomSheetBackdrop>) => {
    const defaultOpacity = 0.3
    return <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} opacity={defaultOpacity} />
  }, [])

  const renderPlayerAvatars = (eligiblePlayerIds: string[]) => (
    <Row>
      {eligiblePlayerIds.map((id: string) => {
        const color = DS.palette[playerColorMap.get(id) || 'Brand/Maroon']
        return (
          <View key={id}>
            <Avatar
              name={players.find((p) => p._id === id)?.name || ''}
              containerStyle={{
                backgroundColor: color
              }}
              textStyle={{
                color: ColorUtils.getContrastColor(color)
              }}
            />
          </View>
        )
      })}
    </Row>
  )

  const renderPotAmount = (amount: number) => (
    <Row alignItems="center" gap={8}>
      <Chips.Icon width={18} height={18} color={DS.palette['Brand/Maroon']} />
      <DS.Text variant="Body Large/Medium" uppercase>
        {amount}
      </DS.Text>
    </Row>
  )

  return (
    <BottomSheetModal
      ref={sidePotSheetRef}
      overrideReduceMotion={ReduceMotion.System}
      enableDynamicSizing={true}
      enableContentPanningGesture={false}
      enablePanDownToClose={false}
      enableHandlePanningGesture={false}
      enableOverDrag={false}
      backdropComponent={renderBackdrop}
      handleIndicatorStyle={{
        backgroundColor: '#0003'
      }}
      backgroundStyle={{ backgroundColor: DS.palette['Brand/Cream'] }}
      containerStyle={{
        marginLeft: !isWeb && marginHorizontal > 0 ? marginHorizontal : undefined,
        shadowColor: DS.palette['Shades/Maroon 2'],
        shadowOffset: {
          width: 0,
          height: 3
        },
        shadowOpacity: 0.27,
        shadowRadius: 4.65,

        elevation: 6
      }}
      onDismiss={() => {
        useSidePotSheetStore.setState({ isOpen: false })
      }}
      style={{
        zIndex: 9999, // Higher than StartGameSheet (10) and other modals
        marginHorizontal: 'auto',
        maxWidth: maxWidth
      }}
    >
      <BottomSheetView
        style={{
          alignItems: 'center',
          justifyContent: 'center',
          flexGrow: 1,
          paddingHorizontal: 16,
          paddingVertical: 12,
          paddingBottom: insets.bottom || 16,
          gap: 16
        }}
      >
        {/* Main Pot Section */}
        {mainPot && (
          <Box width={'100%'}>
            <DS.Text variant="Caption/Bold" uppercase style={{ marginBottom: 8 }}>
              {translate('newGameScreen.common.mainPot')}
            </DS.Text>
            <PotContainer>
              {renderPlayerAvatars(mainPot.eligiblePlayerIds)}
              {renderPotAmount(mainPot.amount)}
            </PotContainer>
          </Box>
        )}

        {/* Side Pots Section */}
        {sidePots.length > 0 && (
          <Box width={'100%'}>
            <DS.Text variant="Caption/Bold" uppercase style={{ marginBottom: 8 }}>
              {translate('newGameScreen.common.sidePotsTitle')}
            </DS.Text>
            <View style={{ gap: 8 }}>
              {sidePots.map((pot: Pot, index: number) => (
                <PotContainer key={`${pot.amount}-${index}`}>
                  {renderPlayerAvatars(pot.eligiblePlayerIds)}
                  {renderPotAmount(pot.amount)}
                </PotContainer>
              ))}
            </View>
          </Box>
        )}

        <DS.Text variant="Body Small/Regular">{translate('newGameScreen.common.sidePots')}</DS.Text>
      </BottomSheetView>
    </BottomSheetModal>
  )
}

const PotContainer = createComponent(View, {
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 24,
  padding: 12,
  paddingHorizontal: 16,
  borderRadius: 4,
  borderWidth: 0.5,
  borderColor: '#ccc'
})
